import { signInSchema, type SignInInput } from '@church/domain'
import { submitSafely, useSendMagicLink, useSignIn } from '@church/data'
import {
  faEnvelope,
  faEnvelopeOpenText,
  faEye,
  faEyeSlash,
  faKey,
  faRightToBracket,
  faUserPlus,
} from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button, ErrorNote, Field, Icon, IconButton, Input } from '../components/ui.tsx'
import { ensureMe } from '../lib/session.ts'
import { afterSignIn, homeUrl, isExternal } from '../lib/site.ts'

export const Route = createFileRoute('/_auth/sign-in')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  component: SignInScreen,
})

function SignInScreen() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const search = Route.useSearch()
  const signIn = useSignIn()
  const magicLink = useSendMagicLink()
  const [magicSentTo, setMagicSentTo] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  // Seconds until another link may be sent (a resend too soon only spams).
  const [cooldown, setCooldown] = useState(0)
  useEffect(() => {
    if (cooldown <= 0) return
    const timer = window.setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [cooldown])

  const form = useForm<SignInInput>({ resolver: zodResolver(signInSchema) })

  const submit = form.handleSubmit(async (input) => {
    await signIn.mutateAsync(input)
    // Back to the app (or page) that sent them here — ForHim addresses only;
    // with none, their home app (ForServices for Sunday-only volunteers).
    const target = afterSignIn(search.redirect, homeUrl(await ensureMe(queryClient)))
    if (isExternal(target)) window.location.assign(target)
    else await navigate({ to: target })
  })

  const sendLink = async () => {
    const email = form.getValues('email')
    const parsed = signInSchema.shape.email.safeParse(email)
    if (!parsed.success) {
      form.setError('email', { message: 'Enter your email to receive a sign-in link' })
      return
    }
    await magicLink.mutateAsync({
      email,
      callbackURL: afterSignIn(search.redirect, window.location.origin),
    })
    setMagicSentTo(email)
    setCooldown(30)
  }
  // The error stays on the mutation, shown below; nothing else to do here.
  const sendLinkSafely = () => void sendLink().catch(() => undefined)
  const wrongPassword =
    (signIn.error as { status?: unknown } | null)?.status === 401 ||
    (signIn.error as { status?: unknown } | null)?.status === 403

  if (magicSentTo) {
    return (
      <div className="screen-in space-y-3 text-center">
        <Icon icon={faEnvelopeOpenText} className="mx-auto size-10 text-content-brand" />
        <h1 className="text-xl font-semibold text-content-default">Check your inbox</h1>
        <p className="text-sm text-content-muted">
          We sent a sign-in link to <span className="font-medium">{magicSentTo}</span>. Open it on
          this device; it expires shortly. Not there? Check the spam folder.
        </p>
        <ErrorNote error={magicLink.error} />
        <div className="flex flex-col gap-2 pt-2">
          <Button
            variant="secondary"
            icon={faEnvelope}
            disabled={cooldown > 0}
            pending={magicLink.isPending}
            pendingLabel="Sending…"
            onClick={sendLinkSafely}
          >
            {cooldown > 0 ? `Resend the link in ${cooldown}s` : 'Resend the link'}
          </Button>
          <Button variant="ghost" onClick={() => setMagicSentTo(null)}>
            Use a different email
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={submitSafely(submit)} className="space-y-4" noValidate>
      <h1 className="text-xl font-semibold text-content-default">Sign in</h1>

      <Field label="Email" error={form.formState.errors.email?.message}>
        <Input type="email" autoComplete="email" {...form.register('email')} />
      </Field>

      <Field label="Password" error={form.formState.errors.password?.message}>
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            className="pr-12"
            {...form.register('password')}
          />
          <IconButton
            icon={showPassword ? faEyeSlash : faEye}
            label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute inset-y-0 right-0 my-auto"
            onClick={() => setShowPassword((shown) => !shown)}
          />
        </div>
      </Field>

      {wrongPassword ? (
        <div
          role="alert"
          className="space-y-1 rounded-control bg-danger-soft px-3 py-2.5 text-sm text-danger-content"
        >
          <p className="font-medium">That email and password don’t match.</p>
          <p>
            Check them, or{' '}
            <button
              type="button"
              className="font-semibold underline underline-offset-2"
              onClick={sendLinkSafely}
            >
              email me a sign-in link instead
            </button>
            .
          </p>
        </div>
      ) : (
        <ErrorNote error={signIn.error ?? magicLink.error} />
      )}

      <Button
        type="submit"
        icon={faRightToBracket}
        className="w-full"
        pending={signIn.isPending}
        pendingLabel="Signing in…"
      >
        Sign in
      </Button>

      <Button
        variant="secondary"
        icon={faEnvelope}
        className="w-full"
        onClick={sendLinkSafely}
        pending={magicLink.isPending}
        pendingLabel="Sending…"
      >
        Email me a sign-in link
      </Button>

      <div className="flex items-center justify-between text-sm">
        <Link
          to="/forgot-password"
          className="inline-flex items-center gap-1.5 text-content-brand hover:underline"
        >
          <Icon icon={faKey} className="size-3.5" />
          Forgot password?
        </Link>
        <Link
          to="/sign-up"
          search={{ redirect: search.redirect }}
          className="inline-flex items-center gap-1.5 text-content-brand hover:underline"
        >
          <Icon icon={faUserPlus} className="size-3.5" />
          Create an account
        </Link>
      </div>
    </form>
  )
}
